import type { Result } from '@/shared/types/result'
import { Ok, Err } from '@/shared/types/result'
import type { ApiError } from '@/shared/types/errors'
import { networkError, authError, serverError, unknownError } from '@/shared/types/errors'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

/**
 * Get Telegram initData for authentication.
 * Returns empty string if not in Telegram WebApp context.
 */
function getInitData(): string {
  try {
    return window.Telegram?.WebApp?.initData ?? ''
  } catch {
    return ''
  }
}

/**
 * Type-safe fetch wrapper that returns Result instead of throwing.
 * All API calls go through this client.
 */
async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<Result<T, ApiError>> {
  const url = `${API_BASE_URL}${path}`
  const initData = getInitData()

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> | undefined),
  }

  // Only set Content-Type to JSON if body is a string (JSON) or not set
  if (!headers['Content-Type'] && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  if (initData && !headers['X-Telegram-Init-Data']) {
    headers['X-Telegram-Init-Data'] = initData
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    })

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        return Err(authError('Authentication failed'))
      }

      const errorBody = await response.text().catch(() => 'Unknown error')
      return Err(serverError(errorBody, response.status))
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return Ok(undefined as T)
    }

    const data: unknown = await response.json()
    return Ok(data as T)
  } catch (error: unknown) {
    if (error instanceof TypeError && 'message' in error) {
      return Err(networkError(error.message))
    }
    return Err(unknownError(String(error)))
  }
}

/**
 * Fetch a binary resource (e.g. image) with Telegram auth header.
 * Returns a blob URL string on success, or null on failure.
 */
export async function fetchAuthBlob(path: string): Promise<string | null> {
  const url = `${API_BASE_URL}${path}`
  const initData = getInitData()

  const headers: Record<string, string> = {
    ...(initData ? { 'X-Telegram-Init-Data': initData } : {}),
  }

  try {
    const response = await fetch(url, { headers })
    if (!response.ok) return null
    const blob = await response.blob()
    return URL.createObjectURL(blob)
  } catch {
    return null
  }
}

/**
 * Build a direct download URL with auth token as query parameter.
 * Used for Telegram WebView where blob downloads don't work.
 */
export function buildAuthDownloadUrl(path: string): string {
  const url = `${API_BASE_URL}${path}`
  const initData = getInitData()
  if (!initData) return url
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}token=${encodeURIComponent(initData)}`
}

export const apiClient = {
  get<T>(path: string): Promise<Result<T, ApiError>> {
    return request<T>(path, { method: 'GET' })
  },

  post<T>(path: string, body?: unknown): Promise<Result<T, ApiError>> {
    return request<T>(path, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  },

  postFormData<T>(path: string, formData: FormData): Promise<Result<T, ApiError>> {
    const initData = getInitData()

    const headers: Record<string, string> = {
      ...(initData ? { 'X-Telegram-Init-Data': initData } : {}),
    }

    // Do NOT set Content-Type - browser will set it with boundary for multipart
    return request<T>(path, {
      method: 'POST',
      body: formData,
      headers,
    })
  },

  put<T>(path: string, body?: unknown): Promise<Result<T, ApiError>> {
    return request<T>(path, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  },

  delete<T>(path: string): Promise<Result<T, ApiError>> {
    return request<T>(path, { method: 'DELETE' })
  },
} as const
